// EAGER header sniffer — the ONLY spatial-decode surface the grid renderer graph imports (plus the
// dependency-free decodeLimits.ts). It performs per-engine header reads (container, geom type, SRID,
// Z/M) + a count-only structural walk capped by MAX_SNIFF_WALK_BYTES; beyond that cap it returns
// header facts only with `pointCount: null`. It MUST NOT import wkx/, any decoder, proj4 or maplibre
// — anything beyond header-sniffing goes through the async lazy API (decodeSpatialBatch / T2).

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialContainer, SpatialDimensions, SpatialGeomType } from './types';
import { MAX_SNIFF_WALK_BYTES } from './decodeLimits';
import { isSpatialTooLargeMarker } from './spatialTooLarge';
import { hexInfo, hexToBytes, sniffWkb, HEADER_PREFIX_BYTES, type SniffFacts } from './sniffWalkers';
import { sniffGpkg, sniffSpatialite } from './sniffContainers';
import { sniffMysqlWkb } from './sniffMysql';
import { sniffMssqlClr, sniffMssqlMarker } from './sniffMssql';
import { sniffSdoMarker } from './sniffOracle';

export type SpatialSniff = {
  container: SpatialContainer;
  geomType: SpatialGeomType | 'Unknown';
  srid: number | null;
  dimensions: SpatialDimensions;
  pointCount: number | null; // null = value over MAX_SNIFF_WALK_BYTES (header facts only)
} | null; // null = does not look spatial

/** Reads container + header facts (+ a capped point walk) for a single cell value. pgsql/mock →
 * EWKB/WKB; mysql → SRID+WKB (T7); mssql → MS-SSCLRT (T8); oracle → parsed SDO marker object (T9,
 * dispatched before the hex path since it is not hex); sqlite → GeoPackage / SpatiaLite. Never
 * throws — anything that does not look spatial (JSON, text, numbers, short/odd hex) returns null. */
const KNOWN_GEOM_TYPES = new Set<SpatialGeomType>([
  'Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon', 'GeometryCollection',
]);

function runWalkers(engine: DatabaseEngineId, value: unknown): SniffFacts | null {
  // Transport too-large placeholder (finding #13): the adapter emitted a `{ __jamSpatialTooLarge }`
  // stub instead of a huge marker → header-only facts (pointCount null so the summary shows the size,
  // never a phantom "N pts"; the glyph still renders via geomType).
  if (isSpatialTooLargeMarker(value)) {
    const p = value.__jamSpatialTooLarge;
    const geomType = KNOWN_GEOM_TYPES.has(p.geomType as SpatialGeomType)
      ? (p.geomType as SpatialGeomType)
      : 'Unknown';
    return {
      container: engine === 'mssql' ? 'mssql-clr' : 'sdo-object',
      geomType,
      srid: null,
      dimensions: 'XY',
      pointCount: null,
      byteLength: p.approxBytes,
    };
  }
  // Oracle SDO arrives as a parsed marker object, not hex — sniff it before the hexInfo() gate.
  if (engine === 'oracle') {
    try {
      return sniffSdoMarker(value);
    } catch {
      return null;
    }
  }
  // MSSQL fork B: a spatial UDT is pre-parsed by the driver into a `{ __jamMssqlGeometry }` marker
  // object (not hex), so sniff it before the hexInfo() gate. A raw CLR buffer (fallback, when the
  // driver did NOT pre-parse) is a hex string → it falls through to sniffMssqlClr below.
  if (engine === 'mssql' && value !== null && typeof value === 'object') {
    try {
      return sniffMssqlMarker(value);
    } catch {
      return null;
    }
  }
  const info = hexInfo(value);
  if (!info) return null;
  const walk = info.byteLength <= MAX_SNIFF_WALK_BYTES;
  const bytes = hexToBytes(info.clean, walk ? Number.POSITIVE_INFINITY : HEADER_PREFIX_BYTES);
  if (!bytes) return null;
  try {
    if (engine === 'pgsql' || engine === 'mock') return sniffWkb(bytes, walk, info.byteLength);
    if (engine === 'mysql') return sniffMysqlWkb(bytes, walk, info.byteLength);
    if (engine === 'mssql') return sniffMssqlClr(bytes, walk, info.byteLength);
    if (engine === 'sqlite') {
      if (bytes[0] === 0x47 && bytes[1] === 0x50) return sniffGpkg(bytes, walk, info.byteLength);
      if (bytes[0] === 0x00) return sniffSpatialite(bytes, walk, info.byteLength);
    }
  } catch {
    return null;
  }
  return null;
}

// `dataType` is part of the public contract (T4+ pass it); this impl is purely structure-driven.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function sniffSpatialValue(engine: DatabaseEngineId, value: unknown, _dataType?: string): SpatialSniff {
  const facts = runWalkers(engine, value);
  if (!facts) return null;
  return {
    container: facts.container,
    geomType: facts.geomType,
    srid: facts.srid,
    dimensions: facts.dimensions,
    pointCount: facts.pointCount,
  };
}

function geomLabel(geomType: SpatialGeomType | 'Unknown', dimensions: SpatialDimensions): string {
  const base = geomType === 'Unknown' ? 'GEOMETRY' : geomType.toUpperCase();
  const suffix = dimensions === 'XYZM' ? ' ZM' : dimensions === 'XYZ' ? ' Z' : dimensions === 'XYM' ? ' M' : '';
  return base + suffix;
}

/** Grid summary built from the sniff, never a full decode:
 * "POLYGON · 24 pts · SRID 4326" · over-cap values: "POLYGON · SRID 4326 · 12.3 MB". */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function summarizeSpatialCell(engine: DatabaseEngineId, value: unknown, _dataType?: string): string | null {
  const facts = runWalkers(engine, value);
  if (!facts) return null;
  const label = geomLabel(facts.geomType, facts.dimensions);
  const sridSeg = facts.srid !== null ? ` · SRID ${facts.srid}` : '';
  if (facts.pointCount === null) {
    const sizeMb = (facts.byteLength / 1048576).toFixed(1);
    return `${label}${sridSeg} · ${sizeMb} MB`;
  }
  const pts = `${facts.pointCount} pt${facts.pointCount === 1 ? '' : 's'}`;
  return `${label} · ${pts}${sridSeg}`;
}
